import { createRouter, createWebHistory } from "vue-router";
import {
  PERMISSIONS,
  TICKET_DETAIL_ROUTE_PERMISSIONS,
  roleHasPermission,
  roleHasAnyPermission,
} from "../config/permissions";

const routes = [
  {
    path: "/login",
    name: "Login",
    component: () => import("../views/Login.vue"),
    meta: { requiresAuth: false },
  },
  {
    path: "/csat/:token",
    name: "PublicCsat",
    component: () => import("../views/PublicCsat.vue"),
    meta: { requiresAuth: false },
  },
  {
    path: "/",
    redirect: "/dashboard",
  },
  {
    path: "/dashboard",
    component: () => import("../views/dashboard/DashboardLayout.vue"),
    meta: { requiresAuth: true },
    children: [
      {
        path: "",
        redirect: { name: "DashboardLive" },
      },
      {
        path: "live",
        name: "DashboardLive",
        component: () => import("../views/dashboard/DashboardLiveView.vue"),
      },
      {
        path: "full",
        name: "DashboardFull",
        component: () => import("../views/Dashboard.vue"),
      },
      {
        path: "csat",
        name: "DashboardCsat",
        component: () => import("../views/dashboard/DashboardCsat.vue"),
      },
      {
        path: "custom",
        name: "DashboardCustom",
        component: () => import("../views/dashboard/DashboardCustom.vue"),
      },
      {
        path: "email-disposition",
        name: "DashboardEmailDisposition",
        component: () => import("../views/dashboard/DashboardEmailDisposition.vue"),
      },
    ],
  },
  {
    path: "/my-dashboard",
    name: "AgentDashboard",
    component: () => import("../views/AgentDashboard.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/create-user",
    name: "CreateUser",
    component: () => import("../views/CreateUser.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.USER_CREATE },
  },
  {
    path: "/admin/users",
    name: "UserManagement",
    component: () => import("../views/admin/UserManagement.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.USER_LIST },
  },
  {
    path: "/admin/smtp-settings",
    name: "SmtpSettingsList",
    component: () => import("../views/admin/SmtpSettingsList.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_SMTP_VIEW },
  },
  {
    path: "/admin/smtp-settings/add",
    name: "SmtpSettingsAdd",
    component: () => import("../views/admin/SmtpSettingsForm.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_SMTP_MANAGE },
  },
  {
    path: "/admin/smtp-settings/:id/edit",
    name: "SmtpSettingsEdit",
    component: () => import("../views/admin/SmtpSettingsForm.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_SMTP_MANAGE },
    props: true,
  },
  {
    path: "/admin/ticket-categories",
    name: "TicketCategoriesL2",
    component: () => import("../views/admin/TicketCategoriesL2.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_CATEGORIES_VIEW },
  },
  {
    path: "/admin/ticket-categories/:l2Id/l3",
    name: "TicketCategoriesL3",
    component: () => import("../views/admin/TicketCategoriesL3.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_CATEGORIES_VIEW },
    props: true,
  },
  {
    path: "/admin/ticket-categories/:l2Id/l3/:l3Id/l4",
    name: "TicketCategoriesL4",
    component: () => import("../views/admin/TicketCategoriesL4.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_CATEGORIES_VIEW },
    props: true,
  },
  {
    path: "/admin/template-settings",
    name: "TemplateSettingsHub",
    component: () => import("../views/admin/TemplateSettingsHub.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_SETTINGS_VIEW },
  },
  {
    path: "/admin/template-settings/reply-templates",
    name: "ReplyTemplatesAdmin",
    component: () => import("../views/admin/ReplyTemplatesAdmin.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_REPLY_TEMPLATES_VIEW },
  },
  {
    path: "/admin/template-settings/ticket-acknowledgement",
    name: "TicketAcknowledgementAdmin",
    component: () => import("../views/admin/TicketAcknowledgementAdmin.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_ACK_VIEW },
  },
  {
    path: "/admin/template-settings/follow-up-templates",
    name: "FollowUpTemplatesAdmin",
    component: () => import("../views/admin/FollowUpTemplatesAdmin.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_FOLLOWUP_TEMPLATES_VIEW },
  },
  {
    path: "/admin/template-settings/ticket-email-filters",
    name: "TicketEmailFiltersAdmin",
    component: () => import("../views/admin/TicketEmailFiltersAdmin.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_EMAIL_FILTERS_VIEW },
  },
  {
    path: "/admin/template-settings/configuration-settings",
    name: "ConfigurationSettingsAdmin",
    component: () => import("../views/admin/ConfigurationSettingsAdmin.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.CONFIGURATION_SETTINGS_VIEW },
  },
  {
    path: "/emails",
    redirect: "/emails/inbox",
    meta: { requiresAuth: true },
  },
  {
    path: "/emails/inbox",
    name: "Inbox",
    component: () => import("../views/Inbox.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/emails/compose",
    name: "ComposeEmail",
    component: () => import("../views/ComposeEmail.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_EMAIL_COMPOSE },
  },
  {
    path: "/emails/emails/:id",
    name: "EmailDetail",
    component: () => import("../views/EmailDetail.vue"),
    meta: { requiresAuth: true },
    props: true,
  },
  {
    path: "/emails/tickets",
    name: "TicketList",
    component: () => import("../views/TicketList.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/emails/tickets/unassign",
    name: "UnassignTicket",
    component: () => import("../views/UnassignTicket.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_LIST_UNASSIGNED },
  },
  {
    path: "/emails/tickets/junk",
    name: "JunkTickets",
    component: () => import("../views/JunkTickets.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_LIST_JUNK },
  },
  {
    path: "/emails/tickets/merged",
    name: "MergedTickets",
    component: () => import("../views/MergedTickets.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_LIST_MERGED },
  },
  {
    path: "/emails/tickets/assigned",
    name: "AllAssignedTickets",
    component: () => import("../views/AllAssignedTickets.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_LIST_ASSIGNED },
  },
  {
    path: "/emails/tickets/completed",
    name: "AllCompletedTickets",
    component: () => import("../views/AllCompletedTickets.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_LIST_ASSIGNED },
  },
  {
    path: "/emails/csat-emails",
    name: "CsatEmailsList",
    component: () => import("../views/CsatEmailsList.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_CSAT_EMAILS_VIEW },
  },
  {
    path: "/emails/tickets/create",
    name: "CreateTicket",
    component: () => import("../views/CreateTicket.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_CREATE_MANUAL },
  },
  {
    path: "/emails/tickets/reports/ticket-report",
    name: "TicketReportGenerate",
    component: () => import("../views/TicketReportGenerate.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_REPORTS_CUSTOMIZED },
  },
  {
    path: "/emails/tickets/reports/assign-to-resolve",
    name: "AssignToResolveReportGenerate",
    component: () => import("../views/AssignToResolveReportGenerate.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_REPORTS_CUSTOMIZED },
  },
  {
    path: "/emails/tickets/reports/ticket-feedback",
    name: "TicketFeedbackReportGenerate",
    component: () => import("../views/TicketFeedbackReportGenerate.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_REPORTS_CUSTOMIZED },
  },
  {
    path: "/emails/tickets/reports/customized",
    redirect: "/emails/tickets/reports",
  },
  {
    path: "/emails/tickets/reports",
    name: "TicketReportsHub",
    component: () => import("../views/TicketReportsHub.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_REPORTS_CUSTOMIZED },
  },
  {
    path: "/emails/tickets/:id",
    name: "TicketDetail",
    component: () => import("../views/TicketDetail.vue"),
    meta: { requiresAuth: true, permissionsAny: TICKET_DETAIL_ROUTE_PERMISSIONS },
    props: true,
  },
  {
    path: "/emails/my-tickets",
    name: "MyAssignedTickets",
    component: () => import("../views/MyAssignedTickets.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_LIST_OWN },
  },
  {
    path: "/emails/my-completed",
    name: "MyCompletedTickets",
    component: () => import("../views/MyCompletedTickets.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_LIST_OWN },
  },
  {
    path: "/emails/mailboxes",
    name: "MailboxList",
    component: () => import("../views/MailboxList.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_MAILBOXES_VIEW },
  },
  {
    path: "/emails/mailboxes/add",
    name: "AddMailbox",
    component: () => import("../views/AddMailbox.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_MAILBOXES_MANAGE },
  },
  {
    path: "/emails/mailboxes/:id/edit",
    name: "EditMailbox",
    component: () => import("../views/AddMailbox.vue"),
    meta: { requiresAuth: true, permission: PERMISSIONS.TICKET_MAILBOXES_MANAGE },
    props: true,
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to, from, next) => {
  const token = localStorage.getItem("token");
  let user = null;
  try {
    user = JSON.parse(localStorage.getItem("user") || "null");
  } catch (e) {
    console.error("Error parsing user from localStorage:", e);
    user = null;
  }

  if (to.meta.requiresAuth && !token) {
    return next("/login");
  }

  if (
    user &&
    (to.meta.permission ||
      (Array.isArray(to.meta.permissionsAny) && to.meta.permissionsAny.length))
  ) {
    const allowed = to.meta.permission
      ? roleHasPermission(user.role, to.meta.permission)
      : roleHasAnyPermission(user.role, to.meta.permissionsAny || []);
    if (!allowed) {
      return next("/dashboard");
    }
  }

  // Explicitly hide all-assigned / all-completed / csat-emails routes from agent users.
  if (
    (to.path === "/emails/tickets/assigned" ||
      to.path === "/emails/tickets/completed" ||
      to.path === "/emails/csat-emails") &&
    user?.role === "agent"
  ) {
    if (to.path === "/emails/tickets/completed") {
      return next("/emails/my-completed");
    }
    return next("/emails/my-tickets");
  }

  if (to.meta.requiresAdmin && user && user.role !== "admin") {
    return next("/dashboard");
  }
  const isDashboardPath =
    to.path === "/dashboard" || to.path.startsWith("/dashboard/");
  if (isDashboardPath && user && user.role === "agent") {
    return next("/my-dashboard");
  }
  if (to.path === "/my-dashboard" && user && user.role !== "agent") {
    return next("/dashboard");
  }
  if (to.path === "/login" && token) {
    return next(user && user.role === "agent" ? "/my-dashboard" : "/dashboard");
  }
  return next();
});

export default router;
